import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const tryScroll = (attempts: number) => {
        const element = document.getElementById(id);
        if (element) {
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        } else if (attempts > 0) {
          setTimeout(() => tryScroll(attempts - 1), 150);
        }
      };
      tryScroll(10);
    }
  }, [location]);

  const handleNavClick = (id: string) => {
    setMobileOpen(false);
    if (location.pathname === '/') {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(`/#${id}`);
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-white/95'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="cursor-pointer block">
              <img
                src="https://public.readdy.ai/ai/img_res/edited_3104d769bd1159b333c68f9b62bd5c48_95d4f063.jpg"
                alt="Company Logo"
                className="h-12 w-auto object-contain"
              />
            </Link>
          </div>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => handleNavClick('home')}
              className="text-gray-700 hover:text-green-600 transition-colors font-medium cursor-pointer whitespace-nowrap"
            >
              Home
            </button>
            <button
              onClick={() => handleNavClick('services')}
              className="text-gray-700 hover:text-green-600 transition-colors font-medium cursor-pointer whitespace-nowrap"
            >
              Services
            </button>
            <button
              onClick={() => handleNavClick('gallery')}
              className="text-gray-700 hover:text-green-600 transition-colors font-medium cursor-pointer whitespace-nowrap"
            >
              Projects
            </button>
            <button
              onClick={() => handleNavClick('testimonials')}
              className="text-gray-700 hover:text-green-600 transition-colors font-medium cursor-pointer whitespace-nowrap"
            >
              Testimonials
            </button>
            <button
              onClick={() => handleNavClick('contact')}
              className="text-gray-700 hover:text-green-600 transition-colors font-medium cursor-pointer whitespace-nowrap"
            >
              Contact
            </button>
          </div>

          {/* Desktop right actions */}
          <div className="hidden md:flex items-center space-x-4">
            <Link
              to="/careers"
              className="bg-green-500 text-white rounded-full px-4 py-2 text-sm font-bold hover:bg-green-600 transition-all cursor-pointer whitespace-nowrap animate-pulse"
            >
              Now Hiring!
            </Link>
            <a
              href="tel:+17087077211"
              className="flex items-center space-x-2 text-green-600 font-semibold hover:text-green-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill text-xl"></i>
              <span className="hidden lg:inline">(708) 707-7211</span>
            </a>
            <span className="hidden lg:inline text-green-600 font-semibold whitespace-nowrap">Ken Svihla</span>
          </div>

          {/* Mobile right: now hiring + hamburger */}
          <div className="flex md:hidden items-center space-x-2">
            <Link
              to="/careers"
              className="bg-green-500 text-white rounded-full px-3 py-1.5 text-xs font-bold hover:bg-green-600 transition-all cursor-pointer whitespace-nowrap animate-pulse"
            >
              Now Hiring!
            </Link>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="flex items-center justify-center w-9 h-9 text-gray-700 hover:text-green-600 transition-colors cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`text-2xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
          />
          {/* Drawer panel */}
          <div className="absolute top-0 right-0 h-full w-72 bg-white shadow-xl flex flex-col pt-20 pb-8 px-6 overflow-y-auto">
            {/* Nav links */}
            <nav className="flex flex-col space-y-1 mb-6">
              {[
                { label: 'Home', id: 'home' },
                { label: 'Services', id: 'services' },
                { label: 'Projects', id: 'gallery' },
                { label: 'Testimonials', id: 'testimonials' },
                { label: 'Contact', id: 'contact' },
              ].map(({ label, id }) => (
                <button
                  key={id}
                  onClick={() => handleNavClick(id)}
                  className="text-left text-gray-700 hover:text-green-600 transition-colors font-medium py-3 border-b border-gray-100 cursor-pointer whitespace-nowrap"
                >
                  {label}
                </button>
              ))}
            </nav>

            {/* Now Hiring */}
            <Link
              to="/careers"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center bg-green-500 text-white rounded-full px-4 py-3 text-sm font-bold hover:bg-green-600 transition-all cursor-pointer whitespace-nowrap animate-pulse mb-4"
            >
              Now Hiring!
            </Link>

            {/* Phone */}
            <a
              href="tel:+17087077211"
              className="flex items-center justify-center space-x-2 text-green-600 font-semibold hover:text-green-700 transition-colors cursor-pointer"
            >
              <i className="ri-phone-fill text-lg"></i>
              <span>(708) 707-7211</span>
            </a>
            <p className="text-center text-green-600 font-semibold mt-2">Ken Svihla</p>
          </div>
        </div>
      )}
    </>
  );
}
